import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ediphorDiagnosticsService } from '@/services/api/ediphorDiagnosticsService';
import { JsonEditor } from './custom/JsonEditor';
import { InputControls } from './custom/InputControls';
import { ResultsDisplay } from './custom/ResultsDisplay';

const TEMPLATE_BASIC = `{
  "measure_time": "${new Date().toISOString()}",
  "pathogen": "FLU_A",
  "device_id": "TEST_DEVICE_001",
  "ip_address": "127.0.0.1",
  "electrode_type": "WP20_4CH",
  "dataset_version": "1.0.0",
  "hardware_version": "1.0.0",
  "firmware_version": "1.0.0",
  "measure_data": {
    "temperature_C": 25.0,
    "humidity_pct": 50.0,
    "battery_V": 3.7,
    "impedance_spectroscopy": {
      "amplitude_mV": 808.8,
      "rtia_Ohm": 5000,
      "frequency_Hz": [10, 100, 1000, 10000],
      "magnitude_Ohm": [[97428, 90000, 82000, 78388], [95000, 88000, 80000, 76000]],
      "phase_rad": [[0.054, 0.045, 0.036, 0.046], [0.052, 0.043, 0.034, 0.044]]
    }
  }
}`;

export function CustomPredict() {
  const [jsonInput, setJsonInput] = useState(TEMPLATE_BASIC);
  const [model, setModel] = useState('');
  const [version, setVersion] = useState('');
  const [result, setResult] = useState<any>(null);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const request = JSON.parse(jsonInput);
      return ediphorDiagnosticsService.submitCustomPredict({
        request,
        model: model || undefined,
        version: version || undefined
      });
    },
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (error: any) => {
      setResult({ error: error.message });
    }
  });

  const handleLoadTemplate = () => {
    setJsonInput(TEMPLATE_BASIC);
  };

  const handleClear = () => {
    setJsonInput('');
  };

  const handleValidate = () => {
    try {
      JSON.parse(jsonInput);
      alert('✅ Valid JSON');
    } catch (error: any) {
      alert(`❌ Invalid JSON: ${error.message}`);
    }
  };

  const validateJsonLocally = () => {
    try {
      JSON.parse(jsonInput);
      return true;
    } catch {
      return false;
    }
  };

  const isValidJson = validateJsonLocally();

  return (
    <div className="space-y-6">
      {/* Input Configuration Section */}
      <InputControls
        model={model}
        onModelChange={setModel}
        version={version}
        onVersionChange={setVersion}
        onLoadTemplate={handleLoadTemplate}
        onClear={handleClear}
      />

      {/* JSON Editor Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-2xl">📝</span>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Request JSON</h3>
        </div>

        <JsonEditor
          value={jsonInput}
          onChange={setJsonInput}
          onValidate={handleValidate}
        />
      </div>

      {/* Submit Button */}
      <div className="flex gap-3">
        <button
          onClick={() => submitMutation.mutate()}
          disabled={!isValidJson || submitMutation.isPending}
          className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors font-semibold flex items-center justify-center gap-2"
        >
          {submitMutation.isPending ? (
            <>
              <span className="animate-spin inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              Submitting...
            </>
          ) : (
            <>
              <span>📤</span>
              Submit Request
            </>
          )}
        </button>
      </div>

      {/* Results Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">📊</span>
            Response
          </h3>
        </div>

        <div className="p-6">
          <ResultsDisplay result={result} />
        </div>
      </div>
    </div>
  );
}
